// src/applications/Presentation.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../components/LocalizedLink";

export default function Presentation() {
  const { t } = useTranslation();

  // Helpers to fetch structured i18n data
  const heroBullets = t("applications.detail.presentation.hero.bullets", {
    returnObjects: true,
  });

  const pricingStarter = t(
    "applications.detail.presentation.pricing.items.starter",
    { returnObjects: true }
  );
  const pricingStandard = t(
    "applications.detail.presentation.pricing.items.standard",
    { returnObjects: true }
  );
  const pricingPlus = t("applications.detail.presentation.pricing.items.plus", {
    returnObjects: true,
  });

  const featureKeys = ["seo", "speed", "analytics", "sections", "cms", "brand"];
  const screenDefs = [
    [
      "screenshot-home.svg",
      "applications.detail.presentation.screens.captions.home",
    ],
    [
      "screenshot-services.svg",
      "applications.detail.presentation.screens.captions.services",
    ],
    [
      "screenshot-about.svg",
      "applications.detail.presentation.screens.captions.about",
    ],
    [
      "screenshot-contact.svg",
      "applications.detail.presentation.screens.captions.contact",
    ],
  ];

  return (
    <main className="bg-white text-slate-900">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/presento/hero-abstract.svg"
            alt=""
            className="h-full w-full object-cover opacity-70"
            loading="eager"
          />
        </div>

        <div className="mx-auto max-w-7xl px-4 pt-20 pb-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100">
                {t("applications.detail.presentation.hero.badge_left")}
                <span className="inline-block size-1 rounded-full bg-indigo-300" />
                {t("applications.detail.presentation.hero.badge_right")}
              </span>

              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                {t("applications.detail.presentation.hero.title")}
              </h1>

              <p className="mt-3 text-slate-600">
                {t("applications.detail.presentation.hero.desc")}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="/presento/screenshot-home.svg"
                  className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-500"
                >
                  {t("applications.detail.presentation.hero.view_demo")}
                </a>

                <LocalizedLink
                  to="/contact"
                  className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
                >
                  {t("applications.detail.presentation.hero.start")}
                </LocalizedLink>
              </div>

              <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-600">
                {Array.isArray(heroBullets) &&
                  heroBullets.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
            </div>

            <div className="relative">
              <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-lg">
                <img
                  src="/presento/hero-mock.svg"
                  alt={t("applications.detail.presentation.hero.mock_alt")}
                  className="w-full rounded-xl object-cover"
                  loading="lazy"
                />
              </div>
              <div className="pointer-events-none absolute -bottom-6 -left-6 hidden w-40 rotate-6 select-none md:block">
                <img src="/presento/sticker-built-react.svg" alt="" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-3xl font-bold">
            {t("applications.detail.presentation.features.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.presentation.features.sub")}
          </p>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featureKeys.map((k) => (
              <Card
                key={k}
                icon={`/presento/feature-${k}.svg`}
                title={t(
                  `applications.detail.presentation.features.items.${k}.title`
                )}
                desc={t(
                  `applications.detail.presentation.features.items.${k}.desc`
                )}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery / Screens */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-3xl font-bold">
            {t("applications.detail.presentation.screens.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.presentation.screens.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {screenDefs.map(([src, capKey]) => (
              <Screenshot
                key={src}
                src={`/presento/${src}`}
                caption={t(capKey)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-3xl font-bold">
            {t("applications.detail.presentation.pricing.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.presentation.pricing.sub")}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Price
              name={pricingStarter.name}
              price={pricingStarter.price}
              bullets={pricingStarter.bullets || []}
            />
            <Price
              featured
              name={pricingStandard.name}
              price={pricingStandard.price}
              bullets={pricingStandard.bullets || []}
            />
            <Price
              name={pricingPlus.name}
              price={pricingPlus.price}
              bullets={pricingPlus.bullets || []}
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-3xl font-bold">
            {t("applications.detail.presentation.demos.title")}
          </h2>
          <p className="mt-2 text-slate-600">
            {t("applications.detail.presentation.demos.sub")}
          </p>

          {/* i18n-powered items */}
          {(() => {
            const demos =
              t("applications.detail.presentation.demos.items", {
                returnObjects: true,
              }) || [];

            return (
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {demos.map((d, i) => (
                  <DemoCard key={i} {...d} />
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-indigo-600 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center">
          <h2 className="text-3xl font-bold">
            {t("applications.detail.presentation.cta.title")}
          </h2>
          <p className="mt-2 text-indigo-100">
            {t("applications.detail.presentation.cta.sub")}
          </p>
          <div className="mt-6 flex justify-center">
            <LocalizedLink
              to="/contact"
              className="rounded-xl bg-white px-6 py-3 font-medium text-indigo-700 hover:bg-indigo-50"
            >
              {t("applications.detail.presentation.cta.start")}
            </LocalizedLink>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ---------- Small presentational components ---------- */

function Card({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-6 bg-white">
      <img src={icon} alt="" className="h-8 w-8" loading="lazy" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{desc}</p>
    </div>
  );
}

function Screenshot({ src, caption }) {
  return (
    <figure className="rounded-2xl border border-slate-200 bg-white p-3">
      <img
        src={src}
        alt={caption}
        className="w-full rounded-xl object-cover"
        loading="lazy"
      />
      <figcaption className="px-2 pb-2 pt-3 text-sm text-slate-600">
        {caption}
      </figcaption>
    </figure>
  );
}

function Price({ name, price, bullets, featured }) {
  return (
    <div
      className={
        "rounded-2xl border p-6 " +
        (featured
          ? "border-indigo-200 bg-indigo-50 shadow-md"
          : "border-slate-200 bg-white")
      }
    >
      <div className="text-sm uppercase tracking-wide text-slate-500">
        {name}
      </div>
      <div className="mt-2 text-3xl font-bold">{price}</div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {(bullets || []).map((b, i) => (
          <li key={i}>• {b}</li>
        ))}
      </ul>
    </div>
  );
}

function DemoCard({ logo, name, blurb, url, tags = [] }) {
  const { t } = useTranslation();

  return (
    <a
      href={url}
      className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md"
      target="_blank"
      rel="noopener noreferrer"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <img
          src={logo}
          alt={name}
          className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200"
          loading="lazy"
        />
        <div className="font-semibold">{name}</div>
      </div>

      {/* Body */}
      <p className="mt-3 text-sm text-slate-600">{blurb}</p>

      {Array.isArray(tags) && tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {tags.map((tTag, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-xs text-slate-700 ring-1 ring-slate-200"
            >
              {tTag}
            </span>
          ))}
        </div>
      )}

      {/* CTA pinned to bottom */}
      <div className="mt-auto pt-5 inline-flex items-center gap-2 text-sm font-medium text-indigo-700">
        {url?.startsWith("/") ? t("common.projects.visit") : t("applications.cta.demo")}
        <svg
          className="size-4 opacity-80 group-hover:translate-x-0.5 transition-transform"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M12.293 3.293a1 1 0 011.414 0l4 4a.997.997 0 01.083 1.32l-.083.094-4 4a1 1 0 01-1.497-1.32l.083-.094L14.586 9H4a1 1 0 01-.117-1.993L4 7h10.586l-2.293-2.293a1 1 0 010-1.414z" />
        </svg>
      </div>
    </a>
  );
}

